/**
 * The one-time card that says the new job page changed (Corbin 10/28: "a calm,
 * one-time notice … they can dismiss. Not the bell."). Inline on the job page,
 * never a pop-up. Shown to people who can use the new page
 * (office_review.manage), on the classic page ("worth a try") and on the new
 * page ("this is what changed"). ✕ hides it for good, on every computer
 * (`user_preferences`, key `job-page.fresh-notice`).
 */
import { useFinishLineSwitch } from "@/lib/finish-line";
import { useUserPreference } from "@/lib/user-preference";

export const FRESH_NOTICE_KEY = "job-page.fresh-notice";

export function FreshNotice({ mode }: { mode: "classic" | "fresh" }) {
    const sw = useFinishLineSwitch();
    const [dismissed, setDismissed, { loaded }] = useUserPreference<boolean>(FRESH_NOTICE_KEY, false);
    if (!sw.allowed || !loaded || dismissed) return null;
    return (
        <div
            className="flex items-start gap-3 rounded-2xl border border-border bg-card px-4 py-3 text-card-foreground"
            data-testid={`fresh-notice-${mode}`}
            role="note"
        >
            <span
                className="grid h-9 w-9 flex-none place-items-center rounded-lg bg-primary text-[13px] font-extrabold text-primary-foreground"
                aria-hidden
            >
                New
            </span>
            <div className="min-w-0 flex-1">
                <p className="m-0 font-bold">The new job page has changed</p>
                <p className="m-0 text-[15px] text-muted-foreground">
                    {mode === "classic"
                        ? "We rebuilt it from scratch, simpler. The top tells you three things: did it pass, whose turn it is, and when. Worth a try."
                        : "We rebuilt it from scratch, simpler. The top tells you three things: did it pass, whose turn it is, and when. The classic page is one click away, top right."}
                </p>
                {mode === "classic" && (
                    <button
                        type="button"
                        className="mt-2 inline-flex min-h-[40px] items-center rounded-lg bg-primary px-3 text-[14px] font-bold text-primary-foreground hover:bg-primary/90"
                        onClick={() => sw.set(true)}
                        data-testid="fresh-notice-try"
                    >
                        Try the new page
                    </button>
                )}
            </div>
            <button
                type="button"
                aria-label="Hide this card"
                className="grid h-9 w-9 flex-none place-items-center rounded-md text-[18px] text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={() => setDismissed(true)}
                data-testid="fresh-notice-dismiss"
            >
                ✕
            </button>
        </div>
    );
}
